import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';

interface User {
  id: number;
  username: string;
  is_admin: number;
}

interface AdminPanelProps {
  token: string;
}

const isProd = process.env.NODE_ENV === 'production';
const BASE_URL = isProd ? '/api/admin/users' : `http://${window.location.hostname}:4658/api/admin/users`;

const AdminPanel: React.FC<AdminPanelProps> = ({ token }) => {
  const [users, setUsers] = useState<User[]>([]);
  const [newUsername, setNewUsername] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [newIsAdmin, setNewIsAdmin] = useState(false);
  const [error, setError] = useState('');

  const fetchUsers = useCallback(async () => {
    try {
      const response = await axios.get(BASE_URL, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUsers(response.data);
    } catch (err) {
      setError('Fehler beim Laden der Benutzer');
    }
  }, [token]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const handleCreateUser = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await axios.post(BASE_URL, 
        { username: newUsername, password: newPassword, is_admin: newIsAdmin },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setNewUsername('');
      setNewPassword('');
      setNewIsAdmin(false);
      fetchUsers();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Fehler beim Erstellen');
    }
  };

  const handleResetPassword = async (id: number) => {
    const pass = prompt('Neues Passwort eingeben:');
    if (!pass) return;
    try {
      await axios.put(`${BASE_URL}/${id}/reset-password`, 
        { newPassword: pass },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      alert('Passwort erfolgreich zurückgesetzt');
    } catch (err) {
      setError('Fehler beim Zurücksetzen');
    }
  };

  const handleToggleAdmin = async (id: number, current: number) => {
    try {
      await axios.put(`${BASE_URL}/${id}/role`, 
        { is_admin: !current },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      fetchUsers();
    } catch (err) {
      setError('Fehler beim Ändern der Rechte');
    }
  };

  const handleDeleteUser = async (id: number) => {
    if (!window.confirm('Benutzer wirklich löschen?')) return;
    try {
      await axios.delete(`${BASE_URL}/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchUsers();
    } catch (err: any) {
      alert(err.response?.data?.error || 'Fehler beim Löschen');
    }
  };

  return (
    <div className="admin-panel" style={{ padding: '20px' }}>
      <h2>Benutzerverwaltung</h2>
      {error && <div style={{ color: 'red', marginBottom: '10px' }}>{error}</div>}
      
      <div className="dashboard-card" style={{ marginBottom: '30px' }}>
        <h3>Neuen Benutzer anlegen</h3>
        <form onSubmit={handleCreateUser} style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
          <input className="input-field" placeholder="Username" value={newUsername} onChange={(e) => setNewUsername(e.target.value)} required />
          <input className="input-field" type="password" placeholder="Passwort" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} required />
          <label style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
            <input type="checkbox" checked={newIsAdmin} onChange={(e) => setNewIsAdmin(e.target.checked)} /> Admin?
          </label>
          <button type="submit" className="btn btn-primary">Anlegen</button>
        </form>
      </div>

      <h3>Aktuelle Benutzer:</h3>
      {users.map(u => (
        <div key={u.id} className="dashboard-card" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
          <div>
            <strong>{u.username}</strong> {u.is_admin ? '(Admin)' : '(Nutzer)'}
          </div>
          <div style={{ display: 'flex', gap: '10px' }}>
            <button className="btn" onClick={() => handleResetPassword(u.id)}>Passwort Reset</button>
            <button className="btn btn-secondary" onClick={() => handleToggleAdmin(u.id, u.is_admin)}>
              {u.is_admin ? 'Admin entziehen' : 'Admin geben'}
            </button>
            <button className="btn btn-danger" onClick={() => handleDeleteUser(u.id)}>Löschen</button>
          </div>
        </div>
      ))}
    </div>
  );
};

export default AdminPanel;
